import type * as THREE from 'three'
import { PROTOTYPE_GLB_URL } from './clips'

/** The parsed prototype asset: the bind-pose scene plus its animation clips. */
export interface PrototypeGltf {
  scene: THREE.Object3D
  animations: THREE.AnimationClip[]
}

let cache: Promise<PrototypeGltf> | null = null

/**
 * Load the shared prototype GLB once and memoise it. All instances clone from
 * this single parse (see createPrototypeInstance), so the asset is fetched and
 * decoded exactly once per session. GLTFLoader is imported dynamically so the
 * loader code is only pulled in when a consumer actually needs the model, and
 * so importing this package for its constants stays cheap.
 *
 * Asset weight: the GLB is emitted by the bundler as a separate fingerprinted
 * asset (via the import.meta.url URL in ./clips.ts), so it is fetched on
 * demand rather than inlined into the JS bundle. It is NOT yet geometry-
 * compressed (2.26 MB uncompressed).
 *
 * Compression follow-up (precise, not a silent skip): meshopt-compress the
 * mesh to cut it to roughly 0.6-0.8 MB, then decode here. Steps:
 *   1. `pnpm add -D @gltf-transform/cli` (or use gltfpack).
 *   2. `gltf-transform meshopt path/to/animated-base-character.glb \
 *        path/to/animated-base-character.glb` (EXT_meshopt_compression
 *        preserves the 45 clips and 53-joint skeleton the rig contract
 *        asserts).
 *   3. Wire the decoder here before loadAsync:
 *        `import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js'`
 *        then `loader.setMeshoptDecoder(MeshoptDecoder)`.
 * Draco is the alternative (three ships DRACOLoader + a decoder under
 * three/examples/jsm/libs/draco/), but meshopt keeps animation data intact
 * and needs no external decoder files served alongside the app.
 *
 * Kept deliberately identical (module body only - the `./clips` import path
 * is necessarily local to each consumer) to its sibling copy at
 * sunder/sstk-sunder-web/src/prototype/src/loader.ts. @sstk/character-schema
 * cannot host this itself (it is explicitly types-only, see its own
 * AGENTS.md), so until a dedicated shared runtime package exists, keeping
 * the two files byte-identical - and porting any fix to both - is what
 * stands in for one. A transient load failure clears the cache so the next
 * caller gets a fresh attempt rather than a permanently memoised rejection.
 */
export function loadPrototypeGltf(): Promise<PrototypeGltf> {
  if (!cache) {
    cache = (async () => {
      const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js')
      const loader = new GLTFLoader()
      const gltf = await loader.loadAsync(PROTOTYPE_GLB_URL)
      return { scene: gltf.scene, animations: gltf.animations }
    })().catch((err: unknown) => {
      cache = null
      throw err
    })
  }
  return cache
}
